/**
 * Shared OTEL config values and pure helper functions.
 * Keep this file dependency-light so tests can import it directly.
 */

import { JourneyIdVersionBBuilder, stringOfLength } from '@vanguard/vg-otel-js/common';
import type { SpanProcessorType as SpanProcessorTypeEnum } from '@vanguard/vg-otel-js/web';

/** Equivalent to vg-otel-js/web's stripQueryStringAndHashFromPath, inlined to avoid ESM-only import in Jest. */
function stripQueryStringAndHashFromPath(url: string): string {
  return url.split('?')[0].split('#')[0];
}

/** SRE browser pattern: `^[a-z0-9]{3}-[a-z0-9_-]+-ui$` */
export const otelServiceName = 'cwp-change-of-ownership-ui';
export const otelAppPrefix = 'CWP';
export const otelBusinessArea = 'retail';
export const otelReferrerMaxLength = 150;

export const otelSpanProcessorType =
  'USE_SIMPLE_AND_HAVE_MORE_REQUESTS_BUT_LOWER_RISK_OF_DROPPED_SPANS' satisfies keyof typeof SpanProcessorTypeEnum;

/**
 * COO ignores:
 * - healthcheck pings
 * - FMF (Fraud Micro-Frontend) — its server does not allow traceparent/tracestate
 *   CORS headers, so OTEL instrumentation breaks FMF's internal XHR/fetch calls
 *   and prevents window.fmfClient from initializing.
 * - ESFT (Fraud services)
 * - smetrics (Adobe Analytics)
 * - corp-pmj (OneTrust cookie consent CDN)
 */
export const ignoreUrls = [
  /\/healthcheck/,
  /^https:\/\/[^/]*fmf-mr\.[^/]+\//,
  /^https:\/\/[^/]*esft\.[^/]+\//,
  /^https:\/\/[^/]*smetrics\.[^/]+\//,
  /^https:\/\/[^/]*corp-pmj\.[^/]+\//,
];

/**
 * Only propagate trace headers to Vanguard origins.
 * NOTE: use an escaped dot to avoid matching "vanguardXcom".
 */
export const propagateTraceHeaderCorsUrls = [/^https:\/\/.*\.vanguard\.com(\/.*)?$/];

const knownFlows = [
  'inheritance',
  'gifting',
  'divorce',
  'internal-transfer',
  'minor-transfer',
  'multi-inheritance',
  'inherited-ira',
  'open-choice',
];

/** Entry pages outside their flow's route prefix. */
const entryPageFlows: Record<string, string> = {
  guide: 'inheritance',
  'gifting-welcome': 'gifting',
  'minor-welcome': 'minor-transfer',
};

const documentChecklistSegment = 'document-checklist';
export const otelUnknownFlow = 'unknown';
export const otelRootStage = 'root';

function toStage(segments: string[]): string {
  return segments.join('_').toLowerCase().replace(/-/g, '_') || otelRootStage;
}

/**
 * Split a URL path or route pattern into flow + stage. Drops `/secure/<flow>/` and
 * `:param` segments to keep `vg.journey_id` under its 60-char cap and params out of baggage.
 */
function parseJourneyRoute(routePath: string): { flow: string; stage: string } {
  const parts = stripQueryStringAndHashFromPath(routePath)
    .split('/')
    .filter((part) => part && !part.startsWith(':'));

  if (parts[0] === 'secure') {
    parts.shift();
  }

  const [first = '', ...rest] = parts;

  if (knownFlows.includes(first)) {
    return { flow: first, stage: toStage(rest) };
  }

  if (first === documentChecklistSegment && knownFlows.includes(rest[0])) {
    return { flow: rest[0], stage: toStage([first]) };
  }

  if (entryPageFlows[first]) {
    return { flow: entryPageFlows[first], stage: toStage([first]) };
  }

  return { flow: otelUnknownFlow, stage: toStage(parts) };
}

export function flowFromUrl(url: string): string {
  return parseJourneyRoute(url).flow;
}

export function stageFromUrl(url: string): string {
  return parseJourneyRoute(url).stage;
}

/** Minimal ActivatedRouteSnapshot shape. */
export interface RouteSnapshotLike {
  routeConfig?: { path?: string } | null;
  firstChild?: RouteSnapshotLike | null;
}

/** Matched route pattern, e.g. `/secure/gifting/method/:position_id`. */
export function routePatternFromSnapshot(root: RouteSnapshotLike): string {
  const segments: string[] = [];
  let node: RouteSnapshotLike | null | undefined = root;

  while (node) {
    const path = node.routeConfig?.path;
    if (path) {
      segments.push(path);
    }
    node = node.firstChild;
  }

  return `/${segments.join('/')}`;
}

export function createBaggageConfig(getLocationHref: () => string, getRoutePattern: () => string) {
  return {
    referrer: () =>
      stringOfLength(stripQueryStringAndHashFromPath(getLocationHref()), 1, otelReferrerMaxLength),

    journeyId: () => {
      const { flow, stage } = parseJourneyRoute(getRoutePattern());

      return JourneyIdVersionBBuilder.create({
        businessArea: otelBusinessArea,
        flow,
        stage,
      });
    },
  };
}
